import { useRef, useLayoutEffect, useEffect } from "react";
import getWindow from "../utils/window";
import { firePageLoadMarketing } from "../utils/marketingTracking/marketingTrackingEvents";
import { useStockPrice } from "../context/stockPrice/StockPriceContext";

const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

// Global state to handle early events before React components mount
let earlyEvent = false;
let componentInstance = null;

// Set up global listener as soon as module loads to catch early events
if (typeof window !== "undefined" && getWindow()) {
  getWindow().asos.eventBus.addListener("pageLoadAnalyticsFired", () => {
    if (componentInstance && componentInstance.handleEvent) {
      componentInstance.handleEvent();
    } else {
      earlyEvent = true;
    }
  });
}

/**
 * Handles the pageLoadAnalyticsFired event to fire marketing analytics with current product data.
 *
 * Race condition fix: Uses a global listener that registers immediately when the module loads,
 * ensuring we never miss the event regardless of React component mount timing. When the component
 * mounts, it takes over handling and uses refs to access the latest product data.
 */
export default function usePageLoadAnalyticsFiredListener() {
  const { products } = useStockPrice();
  const productsRef = useRef(products);
  const hasEventFiredRef = useRef(false);

  productsRef.current = products;

  const handleEvent = () => {
    if (!hasEventFiredRef.current) {
      hasEventFiredRef.current = true;
      firePageLoadMarketing({ products: productsRef.current });
    }
  };

  useIsomorphicLayoutEffect(() => {
    componentInstance = { handleEvent };

    if (earlyEvent) {
      handleEvent();
    }

    return () => {
      componentInstance = null;
    };
  }, []);
}
